import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AccountingStandard } from '../../../models/accountingStandard';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-accounting-standard-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './accounting-standard-list.component.html',
  styleUrl: './accounting-standard-list.component.scss'
})
export class AccountingStandardListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  accountingStandards = signal<AccountingStandard[]>([]);
  rightItem: AccountingStandard | undefined = undefined;
  matchedItems: AccountingStandard[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAccountingStandards();
  }

  loadAccountingStandards(): void {
    this.spinnerService.show();
    this.lutService.getAccountingStandards().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortAccountingStandards(data);
        this.accountingStandards.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading accounting standards:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAccountingStandards(accountingStandards: AccountingStandard[]): AccountingStandard[] {
    return [...accountingStandards].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAccountingStandard(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(accountingStandard: AccountingStandard): void {
  }

  onEditAccountingStandard(accountingStandard: AccountingStandard): void {
  }

  onDeleteAccountingStandard(accountingStandard: AccountingStandard): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(accountingStandard.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalAccountingStandards = this.accountingStandards();

          // 1. Optimistically remove from local state immediately
          this.accountingStandards.update(accountingStandards => accountingStandards.filter(as => as.id !== accountingStandard.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAccountingStandard(accountingStandard.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Accounting standard deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting accounting standard:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.accountingStandards.set(originalAccountingStandards);
              this.toast.error(`<strong>Failed to delete Accounting Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AccountingStandard, newName: string} | {item: AccountingStandard, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: AccountingStandard;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.accountingStandards.update(accountingStandards => {
      const updated = accountingStandards.map(as => as.id === event.item.id ? updatedItem : as);
      return this.sortAccountingStandards(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAccountingStandard(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Accounting standard updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.accountingStandards.update(accountingStandards => {
          const updated = accountingStandards.map(as => as.id === event.item.id ? response : as);
          return this.sortAccountingStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error updating accounting standard:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.accountingStandards.update(accountingStandards =>
          accountingStandards.map(as => as.id === event.item.id ? originalItem : as)
        );
        this.toast.error(`<strong>Failed to update Accounting Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newAccountingStandard: AccountingStandard = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.accountingStandards.update(accountingStandards => this.sortAccountingStandards([...accountingStandards, newAccountingStandard]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAccountingStandard({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Accounting standard created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.accountingStandards.update(accountingStandards => {
          const updated = accountingStandards.map(as => as.id === tempId ? response : as);
          return this.sortAccountingStandards(updated);
        });
      },
      error: (error) => {
        console.error('Error creating accounting standard:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.accountingStandards.update(accountingStandards =>
          accountingStandards.filter(as => as.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Accounting Standard:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AccountingStandard, matchedItems?: AccountingStandard[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAccountingStandardCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalAccountingStandards = this.accountingStandards();

        // 1. Optimistically remove merged items from local state
        this.accountingStandards.update(accountingStandards =>
          accountingStandards.filter(as => !bulkMergeRequest.leftIds.includes(as.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeAccountingStandardsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Accounting standards merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadAccountingStandards();
          },
          error: (error) => {
            console.error('Error merging accounting standards:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.accountingStandards.set(originalAccountingStandards);
            // Restore selection state for retry
            this.rightItem = originalAccountingStandards.find(as => as.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAccountingStandards.filter(as => bulkMergeRequest.leftIds.includes(as.id));
            this.toast.error(`<strong>Failed to merge Accounting Standards:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
